import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
  Button,
  Grid,
  Table,
  IconButton,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from '@material-ui/core';

import Layout from '../Layout/Layout';
import Title from '../shared/Title/Title';
import CurrentPayments from './CurrentPayments';
import PayoffStrategy from './PayoffStrategy';
import { AscendState } from '../../ducks';
import { EDIT_PAYOFF_DEBT } from '../../ducks/actions';
import * as styles from './DebtPayoffTracker.css';
import { Close } from '@material-ui/icons';
import { ResultsController } from './ResultsController';
import { PayoffDebt } from '../../ducks/debtPayoff';
import { differenceInCalendarMonths } from 'date-fns';
import PayoffHowToSection from './PayoffHowToSection';
import { dummyPayoffs } from '../dummyData/dummyPayoffs';
import { UPDATE_DUMMY_PAYOFF_VISIBILITY } from '../../ducks/actions';

interface Calculations {
  loans?: {
    [key: number]: {
      id: string;
      loan_name: string;
      rows: {
        [key: number]: {
          date: string;
          payment: number;
          principal_paid: number;
          interest_paid: number;
          balance_remaining: number;
        };
      };
      starting_balance: number;
      total_date: string;
      total_interest_paid: number;
    };
  };
  totals?: {
    total_date?: string;
    total_interest_paid?: number;
  };
}

type StateProps = {
  payOffDebts?: PayoffDebt[];
  isMobile?: boolean;
  showDummyDataPayoff: boolean;
  userId: number;
};
const mapStateToProps = (state: AscendState) => ({
  payOffDebts: state.payoffDebt.data,
  isMobile: state.layout.isMobile,
  showDummyDataPayoff: state.user.data.showDummyDataPayoff,
  userId: state.user.data.id,
});

type DispatchProps = {
  editPortalDebts: (debts: PayoffDebt[]) => void;
  updateDummyPayoffVisibility: (userId: number) => void;
};
// noinspection JSUnusedGlobalSymbols
const mapDispatchToProps = (dispatch: Dispatch) => ({
  editPortalDebts: (debts: PayoffDebt[]) => dispatch({ type: EDIT_PAYOFF_DEBT, debts }),
  updateDummyPayoffVisibility: (userId: number) =>
    dispatch({ type: UPDATE_DUMMY_PAYOFF_VISIBILITY, payload: { userId } }),
});

type DebtPayoffTrackerProps = StateProps & DispatchProps;
const DebtPayoffTracker = ({
  payOffDebts,
  editPortalDebts,
  updateDummyPayoffVisibility,
  isMobile,
  showDummyDataPayoff,
  userId,
}: DebtPayoffTrackerProps) => {
  const [selectedOption, setSelectedOption] = useState('avalanche');
  useEffect(() => {
    setCalculated(false);
  }, [selectedOption]);

  const [debts, setDebts] = useState([
    {
      name: '',
      current_balance: 0,
      interest_rate: 0,
      minimum_payment: 0,
      status: 'paying',
    },
  ]);
  const [extraPayment, setExtraPayment] = useState(0);
  const [calculated, setCalculated] = useState(false);
  const [formFilled, setFormFilled] = useState(true);
  const [currentTotalPayments, setCurrentTotalPayments] = useState(0);
  const [strategyTotalPayments, setStrategyTotalPayments] = useState(0);
  const [currentDebtFreeMonths, setCurrentDebtFreeMonths] = useState(0);
  const [payoffDebtFreeMonths, setPayoffDebtFreeMonths] = useState(0);
  const strategyMonthlyPayment = useMemo(() => {
    const updatedDebts = [...debts].filter((debt, index) => debt.status === 'paying');
    return updatedDebts.reduce((total: number, payment) => {
      return Number(total) + Number(payment.minimum_payment);
    }, extraPayment);
  }, [debts, extraPayment]);
  const currentMonthlyPayment = useMemo(() => {
    const updatedDebts = [...debts].filter((debt, index) => debt.status === 'paying');
    return updatedDebts.reduce((total: number, payment) => {
      return Number(total) + Number(payment.minimum_payment);
    }, 0);
  }, [debts]);
  const showHeader = showDummyDataPayoff; // conditionally show the payoff how to section tied to showDummyDataPayoff currently

  if (showDummyDataPayoff) {
    payOffDebts = dummyPayoffs;
  }

  // console.log('strategyMonthlyPayment', strategyMonthlyPayment);
  // console.log('currentMonthlyPayment', currentMonthlyPayment);

  const handleDebtChange = (index: number, field: keyof PayoffDebt, value: string | number) => {
    if (['current_balance', 'minimum_payment'].includes(field)) {
      value = parseFloat((value as string).replace(/[^0-9.-]/g, ''));
      if (field === 'interest_rate') {
        value = Math.round((value as number) * 100) / 100;
      }
    }

    const updatedDebts = [...debts];
    updatedDebts[index] = {
      ...updatedDebts[index],
      [field]: value,
    };
    setCalculated(false);
    setDebts(updatedDebts);
  };

  const getMonthsAndTotals = (calculations: Calculations) => {
    let months = 0;
    let totalPayments = 0;
    if (calculations && calculations.totals && calculations.totals.total_date) {
      months = differenceInCalendarMonths(new Date(calculations.totals.total_date), new Date());
    }
    if (calculations && calculations.loans) {
      for (const debt of Object.values(calculations.loans)) {
        if (debt.rows) {
          const rows = Object.values(debt.rows);
          rows.forEach((row: any) => {
            totalPayments += Number(row.payment);
          });
        }
      }
    }
    return { total_months: months, total_payments: totalPayments };
  };

  const calculateDebtPayoff = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isFormValid()) {
      setFormFilled(true);
      setCalculated(true);
      let updatedDebts = debts.map((debt) => ({
        loanName: debt.name,
        currentBalance: debt.current_balance,
        interestRate: debt.interest_rate,
        minimumPayment: debt.minimum_payment,
        status: debt.status,
      }));
      updatedDebts = updatedDebts.filter((debt, index) => debt.status === 'paying');
      const currentCalculations = ResultsController.calculate(
        updatedDebts,
        selectedOption,
        currentMonthlyPayment,
        true
      ) as Calculations;
      // console.log('currentCalculations', currentCalculations);
      const strategyCalculations = ResultsController.calculate(
        updatedDebts,
        selectedOption,
        strategyMonthlyPayment
      ) as Calculations;
      // console.log('strategyCalculations', strategyCalculations);
      const currentMonthsAndTotals = getMonthsAndTotals(currentCalculations);
      const strategyMonthsAndTotals = getMonthsAndTotals(strategyCalculations);
      setPayoffDebtFreeMonths(strategyMonthsAndTotals.total_months);
      setStrategyTotalPayments(strategyMonthsAndTotals.total_payments);
      setCurrentDebtFreeMonths(currentMonthsAndTotals.total_months);
      setCurrentTotalPayments(currentMonthsAndTotals.total_payments);

      editPortalDebts(debts);
    } else {
      setFormFilled(false);
    }
  };

  const handleStatusChange = (index: number, value: string) => {
    handleDebtChange(index, 'status', value);
  };

  const addNewDebt = () => {
    setCalculated(false);
    const newDebtValue = {
      name: '',
      current_balance: 0,
      interest_rate: 0,
      minimum_payment: 0,
      status: 'paying',
    };
    const updatedDebts = [...debts, newDebtValue];
    setDebts(updatedDebts);
  };

  const deleteDebt = (index: number) => {
    const updatedDebts = [...debts];
    updatedDebts.splice(index, 1);
    console.log("After deletion, updatedDebts:", updatedDebts);
    setDebts(updatedDebts);
    editPortalDebts(updatedDebts);
    setCalculated(false);

  };

  const isFormValid = () => {
    return debts.every(
      (debt) => debt.name !== '' && debt.current_balance !== 0 && debt.minimum_payment !== 0
    );
  };
  const getBackgroundColor = (status: string) => {
    if (status === 'paying') {
      return 'green';
    } else if (status === 'paidOff') {
      return '#00838f';
    } else {
      return '';
    }
  };

  // shoving payOffDebts into debts when it changes
  const payoffDebtLength = payOffDebts ? payOffDebts.length : 0;
  useEffect(() => {
    if ((payOffDebts && payOffDebts.length) || (payOffDebts && showDummyDataPayoff)) {
      setDebts(payOffDebts);
    }
  }, [payoffDebtLength]);

  return (
    <Layout>
      {showHeader ? <PayoffHowToSection /> : ''}
      {showDummyDataPayoff ? (
        <div style={{ textAlign: 'center' }}>
          <Button
            size="large"
            variant="contained"
            color="secondary"
            onClick={() => {
              updateDummyPayoffVisibility(userId);
              // alert('Dummy payoff data will be removed.');
            }}
            style={{ marginTop: '20px', marginBottom: '-40px', backgroundColor: '#EF6551' }}>
            Remove Example Payoff Data
          </Button>
        </div>
      ) : (
        ''
      )}
      <Title text="Let's Get You Out Of Debt Now" center />
      <Typography variant="h5" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        Choose Your Debt Payoff Strategy
      </Typography>
      <Grid
        container
        justify="space-evenly"
        alignItems="center"
        direction={isMobile ? 'column' : 'row'}
        className={styles.methodContainer}>
        <Grid item sm={4} style={{ textAlign: 'center' }}>
          <Button
            onClick={() => setSelectedOption('avalanche')}
            className={selectedOption === 'avalanche' ? styles.selectedOption : styles.option}
            size="large"
            variant="contained"
            color="primary"
            style={{ marginBottom: '1rem' }}>
            Avalanche Method
          </Button>
          <Typography
            style={isMobile ? { marginBottom: '1rem' } : {}}
            className={styles.avalancheText}>
            Goal is to tackle highest interest accounts first. This method is often times cheaper
            and faster than Snowball.
          </Typography>
        </Grid>
        <Grid item sm={4} style={{ textAlign: 'center' }}>
          <Button
            onClick={() => setSelectedOption('snowball')}
            className={selectedOption === 'snowball' ? styles.selectedOption : styles.option}
            size="large"
            variant="contained"
            color="primary"
            style={{ marginBottom: '1rem' }}>
            Snowball Method
          </Button>
          <Typography>
            Goal behind this method is to help motivate you by tackling the smallest balances first.
          </Typography>
        </Grid>
      </Grid>
      <form onSubmit={calculateDebtPayoff}>
        <Grid justify="center" className={styles.tableContainer}>
          <Table className={styles.table}>
            <TableHead>
              <TableRow className={styles.tableHeadRow}>
                <TableCell>Debt Name</TableCell>
                <TableCell>Current Balance</TableCell>
                <TableCell>Interest Rate</TableCell>
                <TableCell>Min. Payment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!!debts.length &&
                debts.map((debt, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <input
                        className={isMobile ? styles.debtName : {}}
                        value={debt.name}
                        placeholder="Example: Chase Card"
                        onChange={(e) => handleDebtChange(i, 'name', e.target.value)}></input>
                    </TableCell>
                    <TableCell>
                      <input
                        className={isMobile ? styles.mediumInput : {}}
                        value={isNaN(debt.current_balance) ? '' : debt.current_balance}
                        placeholder="$0"
                        onFocus={(e) => {
                          if (debt.current_balance === 0) {
                            e.target.select();
                          }
                        }}
                        onChange={(e) =>
                          handleDebtChange(i, 'current_balance', e.target.value)
                        }></input>
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.smallInput}
                        value={debt.interest_rate ? debt.interest_rate : 0}
                        placeholder="0%"
                        onFocus={(e) => {
                          if (debt.interest_rate === 0) {
                            e.target.select();
                          }
                        }}
                        step="0.01"
                        onChange={(e) =>
                          handleDebtChange(i, 'interest_rate', e.target.value)
                        }></input>
                    </TableCell>
                    <TableCell>
                      <input
                        className={styles.smallInput}
                        value={debt.minimum_payment ? debt.minimum_payment : 0}
                        placeholder="$0"
                        onFocus={(e) => {
                          if (debt.minimum_payment === 0) {
                            e.target.select();
                          }
                        }}
                        onChange={(e) =>
                          handleDebtChange(i, 'minimum_payment', e.target.value)
                        }></input>
                    </TableCell>
                    <TableCell>
                      <select
                        className={isMobile ? styles.mediumInput : {}}
                        value={debt.status}
                        onChange={(e) => handleStatusChange(i, e.target.value)}
                        style={{
                          color: 'white',
                          backgroundColor: getBackgroundColor(debt.status),
                        }}>
                        <option value="paying">Paying</option>
                        <option value="paidOff">Paid Off</option>
                      </select>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        className={isMobile ? styles.closeButton : {}}
                        onClick={() => deleteDebt(i)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <div className={styles.addDebt}>
            <Button onClick={addNewDebt} variant="text" size="small" color="primary">
              Add another debt
            </Button>
          </div>
          <Grid container justify="center" alignItems="center" direction="column">
            <Typography style={{ textAlign: 'center', marginBottom: '1rem', marginTop: '1rem' }}>
              How much extra do you want to pay towards your debt each month?
            </Typography>
            <input
              value={extraPayment || ''}
              placeholder="0"
              onChange={(e) => {
                const value = e.target.value;
                setExtraPayment(value.trim() === '' ? 0 : parseFloat(value));
              }}></input>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              style={{ marginTop: '2rem' }}>
              Save & Calculate Plan
            </Button>
            {!formFilled && (
              <Typography style={{ color: 'red', marginTop: '1rem' }}>
                Please fill out all fields.
              </Typography>
            )}
          </Grid>
        </Grid>
      </form>
      {calculated ? (
        <Grid container spacing={16} className={styles.resultsContainer}>
          <Grid item md={6} xs={12} style={{ height: '100%' }}>
            <CurrentPayments debts={debts} currentDebtFreeMonths={currentDebtFreeMonths} />
          </Grid>
          <Grid item md={6} xs={12} style={{ height: '100%' }}>
            <PayoffStrategy
              debts={debts}
              option={selectedOption}
              extraPayment={extraPayment}
              currentTotalPayments={currentTotalPayments}
              strategyTotalPayments={strategyTotalPayments}
              payoffDebtFreeMonths={payoffDebtFreeMonths}
              currentDebtFreeMonths={currentDebtFreeMonths}
            />
          </Grid>
        </Grid>
      ) : (
        ''
      )}
    </Layout>
  );
};

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(DebtPayoffTracker);
